import React, { useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const keyframeCSS = `
  @keyframes revealSparkle {
    0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
    100% { transform: translate(var(--tx), var(--ty)) rotate(720deg) scale(0); opacity: 0; }
  }
  @keyframes revealBurst {
    0% { transform: scale(0); opacity: 0.6; }
    100% { transform: scale(4); opacity: 0; }
  }
  @keyframes revealPulseRing {
    0% { transform: scale(0.8); opacity: 0.5; }
    100% { transform: scale(2.5); opacity: 0; }
  }
  @keyframes spotlightReveal {
    0% { opacity: 0; }
    30% { opacity: 1; }
    100% { opacity: 0.6; }
  }
  @keyframes continueFade {
    0%, 60% { opacity: 0; }
    100% { opacity: 0.6; }
  }
  @keyframes shimmerText {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
`;

function formatValue(value) {
  if (typeof value === 'number') {
    return value.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: value < 1 ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
  return String(value);
}

function getValueTier(value) {
  if (typeof value !== 'number') return 'low';
  if (value >= 10000) return 'high';
  if (value >= 100) return 'medium';
  return 'low';
}

function getTierColors(tier) {
  switch (tier) {
    case 'high':
      return {
        text: '#D4AF37',
        glow: 'rgba(212,175,55,0.8)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(212,175,55,0.15) 0%, rgba(100,50,0,0.05) 40%, transparent 70%)',
        shadow: '0 0 40px rgba(212,175,55,0.6), 0 0 80px rgba(212,175,55,0.3), 0 0 120px rgba(212,175,55,0.15)',
        particle: '#F0D060',
      };
    case 'medium':
      return {
        text: '#F0A030',
        glow: 'rgba(240,160,48,0.6)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(240,160,48,0.1) 0%, transparent 60%)',
        shadow: '0 0 30px rgba(240,160,48,0.4), 0 0 60px rgba(240,160,48,0.2)',
        particle: '#F0C060',
      };
    default:
      return {
        text: '#3498db',
        glow: 'rgba(52,152,219,0.5)',
        bgRadial: 'radial-gradient(ellipse at center, rgba(52,152,219,0.08) 0%, transparent 60%)',
        shadow: '0 0 20px rgba(52,152,219,0.3), 0 0 40px rgba(52,152,219,0.15)',
        particle: '#5dade2',
      };
  }
}

function Sparkle({ delay, tx, ty, size, color, duration }) {
  return (
    <div
      style={{
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        backgroundColor: color,
        boxShadow: `0 0 ${size * 2}px ${color}`,
        animation: `revealSparkle ${duration}s ease-out ${delay}s forwards`,
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    />
  );
}

export default function RevealAnimation({ isVisible, boxNumber, prizeValue, prizeLabel, onComplete, autoClose = false }) {
  // Auto-close after delay (for live/spectator mode)
  // Note: onComplete intentionally excluded from deps to prevent
  // polling re-renders from resetting the timer
  useEffect(() => {
    if (!isVisible || !autoClose) return;
    const timer = setTimeout(() => {
      onComplete?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [isVisible, autoClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayText = prizeLabel && (prizeValue === 0 || prizeValue === undefined) ? prizeLabel : null;
  const tier = displayText ? 'medium' : getValueTier(prizeValue);
  const colors = getTierColors(tier);

  const sparkles = useMemo(() => {
    const items = [];
    const count = tier === 'high' ? 16 : tier === 'medium' ? 10 : 6;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const dist = 60 + Math.random() * 120;
      items.push({
        id: i,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist - 30,
        size: 3 + Math.random() * 5,
        delay: 0.4 + Math.random() * 0.5,
        duration: 1 + Math.random() * 0.8,
      });
    }
    return items;
  }, [tier]);

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(2, 2, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    cursor: autoClose ? 'default' : 'pointer',
    overflow: 'hidden',
  };

  const spotlightStyle = {
    position: 'absolute',
    inset: 0,
    background: colors.bgRadial,
    animation: 'spotlightReveal 1s ease-out forwards',
    pointerEvents: 'none',
  };

  const boxNumberStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
    fontWeight: 600,
    color: 'rgba(200,200,220,0.8)',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginBottom: '16px',
    zIndex: 5,
  };

  const prizeContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  };

  const prizeTextStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: tier === 'high' ? 'clamp(3.5rem, 10vw, 6rem)' : 'clamp(3rem, 8vw, 5rem)',
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: colors.text,
    textShadow: colors.shadow,
    zIndex: 5,
    position: 'relative',
    ...(tier === 'high' ? {
      background: 'linear-gradient(90deg, #B8960C, #D4AF37, #F0D060, #D4AF37, #B8960C)',
      backgroundSize: '200% auto',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      animation: 'shimmerText 3s linear infinite',
    } : {}),
  };

  // Burst ring behind the prize
  const burstRingStyle = {
    position: 'absolute',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    border: `2px solid ${colors.glow}`,
    animation: 'revealPulseRing 1.5s ease-out 0.3s forwards',
    opacity: 0,
    pointerEvents: 'none',
  };

  const burstRingStyle2 = {
    ...burstRingStyle,
    animation: 'revealPulseRing 1.5s ease-out 0.6s forwards',
  };

  const continueStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    color: 'rgba(160,160,192,0.6)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginTop: '40px',
    animation: 'continueFade 2s ease-in forwards',
    zIndex: 5,
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          style={overlayStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={autoClose ? undefined : onComplete}
        >
          <style>{keyframeCSS}</style>

          {/* Spotlight background */}
          <div style={spotlightStyle} />

          {/* Box number */}
          <motion.div
            style={boxNumberStyle}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            Pacco n. {boxNumber}
          </motion.div>

          {/* Prize value with effects */}
          <div style={prizeContainerStyle}>
            {/* Burst rings */}
            <div style={burstRingStyle} />
            <div style={burstRingStyle2} />

            {/* Sparkle particles */}
            {sparkles.map((s) => (
              <Sparkle
                key={s.id}
                tx={s.tx}
                ty={s.ty}
                size={s.size}
                delay={s.delay}
                duration={s.duration}
                color={colors.particle}
              />
            ))}

            {/* Prize amount */}
            <motion.div
              style={prizeTextStyle}
              initial={{ scale: 0, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 180,
                damping: 12,
                delay: 0.4,
              }}
            >
              {displayText || formatValue(prizeValue)}
            </motion.div>
          </div>

          {/* Continue label */}
          {!autoClose && (
            <div style={continueStyle}>
              Tocca per continuare
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
